import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../Common/Axios/axiosInstance";
import API from "../../Common/Endpoint/serviceConstrants";

const sortProductsByDisplayOrder = (products = []) =>
  [...products].sort((a, b) => {
    const aOrder = Number(a?.displayOrder);
    const bOrder = Number(b?.displayOrder);
    const normalizedA = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
    const normalizedB = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;

    return normalizedA - normalizedB;
  });

const normalizeProductListResponse = (data = {}, useDisplayOrder = true) => {
  const responseData = Array.isArray(data?.response)
    ? data.response[0] || {}
    : data?.response || {};
  const products = responseData?.products || [];

  return {
    ...data,
    response: {
      ...responseData,
      products: useDisplayOrder
        ? sortProductsByDisplayOrder(products)
        : products,
      totalCount: responseData?.totalCount || 0,
    },
  };
};

export const getProductList = createAsyncThunk('getProductList', async (payload = {}) => {
  try {
    // Destructure and prepare params
    const {
      categoryId,
      pageNumber,
      pageSize,
      sortOrder,
      minDiscount,
      maxDiscount,
      search,
      searchByBrand,
      productId,
      isFeatured,
      redeemable,
      redemptionType,
      discountSlab,
      sortBy,
    } = payload;

    const selectedCategoryId = Array.isArray(categoryId)
      ? categoryId.join(",")
      : categoryId;
    const isProductLookup = !!productId;
    const params = {};
    if (!isProductLookup && selectedCategoryId) params.categoryId = selectedCategoryId;
    if (pageNumber) params.pageNumber = pageNumber;
    if (pageSize) params.pageSize = pageSize;
    const selectedRedemptionType = redemptionType || redeemable;
    const selectedSortOrder = sortBy || sortOrder;

    if (selectedSortOrder) params.sortOrder = selectedSortOrder;
    if (!discountSlab && minDiscount !== undefined && minDiscount !== null) params.minDiscount = minDiscount;
    if (!discountSlab && maxDiscount !== undefined && maxDiscount !== null) params.maxDiscount = maxDiscount;
    if (!isProductLookup && search) params.search = search;
    if (!isProductLookup && searchByBrand) params.searchByBrand = searchByBrand;
    if (isProductLookup) params.productId = productId;
    if (isFeatured) params.isFeatured = isFeatured;
    if (selectedRedemptionType) params.redemptionType = selectedRedemptionType;
    if (discountSlab) params.discountSlab = discountSlab;

    const response = await axiosInstance.get(API.productList, { params });
    return normalizeProductListResponse(response.data, !sortBy);
  } catch (error) {
    console.error(error);
    return Promise.reject(error.message);
  }
});

export const getProductListTrending = createAsyncThunk('getProductListTrending', async (payload = {}) => {
  try {
    const {
      categoryId,
      pageNumber,
      pageSize,
      sortOrder,
      minDiscount,
      maxDiscount,
      specialId,
      redeemable,
      redemptionType,
      discountSlab,
      sortBy,
    } = payload;
    const params = {};
    const selectedRedemptionType = redemptionType || redeemable;
    const selectedSortOrder = sortBy || sortOrder;

    if (categoryId) params.categoryId = Array.isArray(categoryId) ? categoryId.join(",") : categoryId;
    if (pageNumber) params.pageNumber = pageNumber;
    if (pageSize) params.pageSize = pageSize;
    // if (sortOrder) params.sortOrder = sortOrder;
    // if (minDiscount) params.minDiscount = minDiscount;
    // if (maxDiscount) params.maxDiscount = maxDiscount;
    if (specialId) params.specialId = specialId;
    if (selectedRedemptionType) params.redemptionType = selectedRedemptionType;
    if (discountSlab) params.discountSlab = discountSlab;
    if (selectedSortOrder) params.sortOrder = selectedSortOrder;
    const response = await axiosInstance.get(API.trendingProducts, { params });
    return response.data; // Return only the data, not the whole response
  } catch (error) {
    return Promise.reject(error.message);
  }
});

export const productSlice = createSlice({
  name: "product",
  initialState: {
    isLoading: false,
    data: [],
    message: "",
  },
  reducers: {
    onProductByIdSubmit: (state) => {
      return {
        ...state,
        productById: [],
        message: "",
        status_code: null,
        isLoading: false,
      };
    },

    onProductByIdSuccess: (state, { payload }) => {
      const { data, message, status_code = 200 } = payload;
      return {
        ...state,
        productById: data,
        message: message,
        status_code: status_code,
        isLoading: false,
      };
    },

    onProductByIdError: (state, { payload }) => {
      const { message, status_code = 400 } = payload;
      return {
        ...state,
        productById: [],
        message: message,
        status_code: status_code,
        isLoading: false,
      };
    },
    onProductByProductIdSubmit: (state) => {
      return {
        ...state,
        productByProductId: [],
        message: "",
        status_code: null,
        isLoading: true,
      };
    },

    onProductByProductIdIdSuccess: (state, { payload }) => {
      const { data, message, status_code = 200 } = payload;
      return {
        ...state,
        productByProductId: data,
        message: message,
        status_code: status_code,
        isLoading: false,
      };
    },

    onProductByProductIdIdError: (state, { payload }) => {
      const { message, status_code = 400 } = payload;
      return {
        ...state,
        productByProductId: [],
        message: message,
        status_code: status_code,
        isLoading: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProductList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.response?.products;
        state.message = "Success";
      })
      .addCase(getProductList.rejected, (state, action) => {
        state.isLoading = false;
        state.data = [];
        state.message = action.error.message;
      })
      .addCase(getProductListTrending.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProductListTrending.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.response?.products;
        state.message = "Success";
      })
      .addCase(getProductListTrending.rejected, (state, action) => {
        state.isLoading = false;
        state.data = [];
        state.message = action.error.message;
      });
  },
});
export const { onProductByIdSubmit, onProductByIdSuccess, onProductByIdError, onProductByProductIdSubmit, onProductByProductIdIdSuccess, onProductByProductIdIdError } =
  productSlice.actions;

export default productSlice.reducer;
